/// <reference types="vite/client" />
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { Provider as ReduxProvider } from 'react-redux';
import { ClerkProvider, useAuth } from '@clerk/react';
import { ConvexProviderWithClerk } from 'convex/react-clerk';
import { ConvexReactClient } from 'convex/react';
import { store } from './store';
import { routeTree } from './routeTree.gen';
import { GraphErrorToastHost } from './lib/graph/useGraphErrorToast';
import { useLocalEditQueueDrain } from './lib/edits/useLocalEditQueueDrain';
import { useMsalSessionBinding } from './lib/graph/msalSession';
import { rotateShowcaseFallbackSessionId } from './lib/showcase/showcaseSession';
import { ConflictDialogHost } from './components/sharepoint/ConflictDialogHost';
import {
  handlePortalSignInResponse,
  isPortalSignInResponse,
} from './lib/tenant/portalSignInRedirect';
import { acquireInstance } from './lib/tenant/msalInstanceRegistry';
import {
  installConvexClientForTests,
  installTestHooks,
} from './lib/test-hooks';
import { api } from '../convex/_generated/api';
import './styles/app.css';

// `?nuke=1` debug escape hatch — wipe local + session storage + IndexedDB
// on load. Use when a stale Redux/MSAL/IDB state causes a reload loop
// during dev. Gated on the query flag so normal dev sessions are
// preserved across reloads.
if (
  typeof window !== "undefined" &&
  new URLSearchParams(window.location.search).has("nuke")
) {
  try { window.localStorage.clear(); } catch {}
  try { window.sessionStorage.clear(); } catch {}
  try {
    if ("indexedDB" in window && typeof indexedDB.databases === "function") {
      indexedDB.databases().then((dbs) => {
        for (const db of dbs) {
          if (typeof db.name === "string") indexedDB.deleteDatabase(db.name);
        }
      }).catch(() => {});
    }
  } catch {}
  console.warn("[main] storage cleared via ?nuke=1");
}

// J — install test hooks if running in test mode (?test=1).
// Self-gated; no-op in production. See packages/repancy/docs/testing/
// playwright-strategy.md for the hook catalog + use cases.
installTestHooks();

function LocalEditQueueDrainer() {
  useLocalEditQueueDrain();
  return null;
}

/**
 * F19.3 — bind MSAL lifecycle to Clerk session lifecycle. Clears MSAL
 * cache + removeAccount + drops `repancy:` localStorage on Clerk
 * sign-out / user-swap so MSAL state can't leak across users on a
 * shared browser. Mount inside `<ClerkProvider>` (the hook reads
 * Clerk context via `useUser`). No-op on the first observation +
 * sign-in transitions (preserves the existing first-touch UX).
 *
 * F19.6 — register an `onClear` callback that explicitly rotates the
 * `repancy:showcaseFallbackSessionId` key. The default
 * `clearMsalSession` already drops every `repancy:`-prefixed key on
 * a clear pass, so this is belt-and-braces: explicit telemetry +
 * survives any future `preserveKeys` configuration that might
 * accidentally include the fallback id.
 */
function MsalSessionBinder() {
  useMsalSessionBinding({
    onClear: (_result, transition) => {
      const rotated = rotateShowcaseFallbackSessionId();
      if (rotated) {
        // eslint-disable-next-line no-console
        console.info(
          '[F19.6] rotated showcase fallback session id on Clerk',
          transition,
        );
      }
    },
  });
  return null;
}

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string;

if (!PUBLISHABLE_KEY) {
  throw new Error('Missing VITE_CLERK_PUBLISHABLE_KEY in environment');
}

// Minimal Clerk theming. We use the prebuilt `<SignIn />` + `<SignUp />`
// components inside our own AuthLayout chrome. Per CLAUDE.md, keep
// this thin — the only required overrides are button colors so the
// primary submit + social buttons are readable on Clerk's default
// near-white card. Do NOT hide the footer cross-link here: `/`
// (SignUp) and `/sign-in` (SignIn) are real sibling routes and the
// bottom-of-card link is how users move between them.
const CLERK_APPEARANCE = {
  elements: {
    cardBox: '!shadow-none',
    card: '!bg-[#f2f2f2] !shadow-none !border-0 !rounded-none',
    formButtonPrimary:
      'cl-formButtonPrimary !bg-[#316ac5] !text-white !border !border-[#1a4a8a] !shadow-sm hover:!bg-[#3d7ad6] !font-semibold !rounded-sm',
    socialButtonsBlockButton:
      'cl-socialButtonsBlockButton !text-black !border !border-[#808080] !bg-white hover:!bg-[#f0f0f0] !rounded-sm',
    formFieldInput:
      '!min-h-[42px] !py-[10px] !px-[10px] !text-sm',
    footer: '!bg-[#f2f2f2]',
  },
} as const;

/**
 * Clerk's English defaults are fine on the sign-up side ("Create your
 * account"). Sign-in defaults to "Welcome back!" which reads slightly
 * presumptuous for returning visitors who may have only signed in
 * once before — override the subtitle to a neutral string so both
 * surfaces (`/` SignUp, `/sign-in` SignIn) feel deliberate. Keys we
 * don't set fall through to Clerk's defaults.
 */
const CLERK_LOCALIZATION = {
  signIn: {
    start: {
      title: 'Sign in to Repancy',
      subtitle: 'Use your Microsoft or Google account, phone, or email.',
    },
  },
} as const;

const convex = new ConvexReactClient(
  import.meta.env.VITE_CONVEX_URL as string,
);

// J — Stash the Convex client + api on window so Phase J test hooks
// (lib/test-hooks/index.ts) can drive the queue/audit reads without
// the spec needing access to the Convex provider tree. Self-gated:
// no-op unless ?test=1.
installConvexClientForTests(convex);
if (
  typeof window !== 'undefined' &&
  new URLSearchParams(window.location.search).get('test') === '1'
) {
  (window as unknown as { __repancyConvexApi?: unknown }).__repancyConvexApi =
    api;
}

const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById('root')!;

const appTree = (
  <StrictMode>
    <ClerkProvider
      publishableKey={PUBLISHABLE_KEY}
      appearance={CLERK_APPEARANCE}
      localization={CLERK_LOCALIZATION}
    >
      <MsalSessionBinder />
      <ConvexProviderWithClerk client={convex} useAuth={useAuth}>
        <ReduxProvider store={store}>
          <RouterProvider router={router} />
        </ReduxProvider>
      </ConvexProviderWithClerk>
    </ClerkProvider>
    <GraphErrorToastHost />
    <LocalEditQueueDrainer />
    <ConflictDialogHost />
  </StrictMode>
);

// F16.L — Portal sign-in redirect recovery. When the SPA returns from
// login.microsoftonline.com with `#code=...`, TanStack Router would
// strip the hash before MSAL could read it. Detect that case here +
// hand the hash to MSAL via handleRedirectPromise BEFORE React boots,
// then history.replaceState the URL back to the portal route so the
// router sees the original /c/{slug}/desktop path.
async function bootSignInRecovery(): Promise<void> {
  if (!isPortalSignInResponse()) return;
  const recovery = await handlePortalSignInResponse({
    acquireInstance: async (lockedTenantId) =>
      (await acquireInstance(lockedTenantId)) as unknown as {
        handleRedirectPromise: () => Promise<{ account: unknown } | null>;
        setActiveAccount: (account: unknown) => void;
      },
  });
  if (recovery !== null) {
    history.replaceState(null, '', recovery.returnTo);
  }
}

void bootSignInRecovery().finally(() => {
  createRoot(rootElement).render(appTree);
});
